import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  EditorContent,
  useEditor,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import FontSize from "tiptap-extension-font-size";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import CharacterCount from "@tiptap/extension-character-count";
import { NodeSelection } from "@tiptap/pm/state";

const AIANTA_PALETTE = [
  "#181e20", "#2c353b", "#1a2229", "#80898b",
  "#ffffff", "#00789e", "#007da4", "#007ea5",
  "#88c3d5", "#37cc84", "#667f35", "#cb8b19",
  "#eab950", "#e38994", "#cb283c", "#4196e0",
];

const FONT_SIZES = ["14px", "16px", "18px", "20px", "24px", "30px"];

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Open Sans", value: "Open Sans, sans-serif" },
  { label: "Montserrat", value: "Montserrat, sans-serif" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "Times New Roman, serif" },
];

const imageAlignStyles = {
  left: {
    float: "left",
    marginRight: "1em",
    marginBottom: "0.75em",
  },
  right: {
    float: "right",
    marginLeft: "1em",
    marginBottom: "0.75em",
  },
  center: {
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: "0.75em",
  },
};

const IMAGE_WIDTH_PRESETS = [
  { label: "25%", value: "25%" },
  { label: "50%", value: "50%" },
  { label: "75%", value: "75%" },
  { label: "100%", value: "100%" },
];

const MIN_IMAGE_WIDTH = 80;
const MAX_IMAGE_WIDTH = 1200;

function clampImageWidth(width) {
  return Math.min(MAX_IMAGE_WIDTH, Math.max(MIN_IMAGE_WIDTH, width));
}

function stylePropertyValue(style, property) {
  const declarations = style
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean);

  const match = declarations.find((declaration) => {
    const [name] = declaration.split(":").map((part) => part.trim().toLowerCase());
    return name === property;
  });

  if (!match) return null;

  return match
    .split(":")
    .slice(1)
    .join(":")
    .replace(/\s*!important\s*$/i, "")
    .trim();
}

function normalizeImageSize(value, { clampPixels = true } = {}) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const rounded = Math.round(value);
    return clampPixels ? clampImageWidth(rounded) : Math.max(1, rounded);
  }

  const stringValue = String(value).trim();
  const percentMatch = stringValue.match(/^(\d+(?:\.\d+)?)%$/);
  if (percentMatch) {
    const percent = Math.min(100, Math.max(1, parseFloat(percentMatch[1])));
    return `${percent}%`;
  }

  const pixelMatch = stringValue.match(/^(\d+(?:\.\d+)?)(?:px)?$/i);
  if (pixelMatch) {
    const pixels = Math.round(parseFloat(pixelMatch[1]));
    return clampPixels ? clampImageWidth(pixels) : Math.max(1, pixels);
  }

  return null;
}

function cssSizeValue(value, options = {}) {
  const normalized = normalizeImageSize(value, options);
  if (normalized === null) return undefined;
  return typeof normalized === "number" ? `${normalized}px` : normalized;
}

function pixelWidthValue(value) {
  const normalized = normalizeImageSize(value);
  return typeof normalized === "number" ? normalized : "";
}

function styleObjectToString(styleObject) {
  return Object.entries(styleObject)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}: ${value}`)
    .join("; ");
}

function wrapperStyleForAlign(align) {
  return imageAlignStyles[align] || {};
}

function unwrapElement(element) {
  const parent = element.parentNode;
  if (!parent) return;

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

function imageAlignFromElement(element) {
  if (!element) return null;

  const dataAlign = element.getAttribute("data-align");
  if (dataAlign) return dataAlign;

  const className = element.getAttribute("class") || "";
  if (/\bfr-fil\b/.test(className)) return "left";
  if (/\bfr-fir\b/.test(className)) return "right";
  if (/\bfr-fic\b/.test(className)) return "center";

  const style = element.getAttribute("style") || "";
  if (/float:\s*left/i.test(style)) return "left";
  if (/float:\s*right/i.test(style)) return "right";
  if (/margin-left:\s*auto/i.test(style) && /margin-right:\s*auto/i.test(style)) return "center";

  return null;
}

function legacyImageWrappers(img) {
  const wrappers = [];
  let element = img.parentElement;

  while (element && element !== img.ownerDocument.body) {
    if (
      element.matches("figure, .fr-img-caption, .fr-img-wrap, [data-align]") ||
      /\bfr-fi[clr]\b/.test(element.getAttribute("class") || "") ||
      /float:\s*(left|right)/i.test(element.getAttribute("style") || "")
    ) {
      wrappers.push(element);
    }

    element = element.parentElement;
  }

  return wrappers;
}

function normalizeLegacyImageMarkup(doc) {
  doc.querySelectorAll("img").forEach((img) => {
    const wrappers = legacyImageWrappers(img);
    const align = imageAlignFromElement(img) || wrappers.map(imageAlignFromElement).find(Boolean);
    if (align) img.setAttribute("data-align", align);

    const imgStyle = img.getAttribute("style") || "";
    const wrapperStyle = wrappers
      .map((wrapper) => wrapper.getAttribute("style") || "")
      .find((style) => stylePropertyValue(style, "width")) || "";
    const width = stylePropertyValue(imgStyle, "width") || stylePropertyValue(wrapperStyle, "width");
    const height = stylePropertyValue(imgStyle, "height");
    const allowed = [];

    if (width) allowed.push(`width: ${width}`);
    if (height) allowed.push(`height: ${height}`);
    if (align && align !== "none") allowed.push(styleObjectToString(wrapperStyleForAlign(align)));

    if (allowed.length) img.setAttribute("style", allowed.join("; "));
  });
}

function sanitizeEditorHtml(html) {
  if (!html) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  doc.querySelectorAll("script, style, meta, link, object, embed").forEach((element) => element.remove());
  doc.querySelectorAll('[data-f-id="pbf"]').forEach((element) => element.remove());
  doc.querySelectorAll("button, form").forEach(unwrapElement);
  doc.querySelectorAll("input, textarea, select, option").forEach((element) => element.remove());
  doc.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (/^on/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  normalizeLegacyImageMarkup(doc);

  return doc.body.innerHTML;
}

// --- ResizableImage Extension ---
function ResizableImageComponent({ node, updateAttributes, selected, editor, getPos }) {
  const imgRef = useRef(null);
  const [localSize, setLocalSize] = useState(() => ({
    width: node.attrs.width,
    height: node.attrs.height,
  }));
  const [isResizing, setIsResizing] = useState(false);
  const dragState = useRef(null);

  useEffect(() => {
    if (isResizing) return;
    setLocalSize({
      width: node.attrs.width,
      height: node.attrs.height,
    });
  }, [isResizing, node.attrs.height, node.attrs.width]);

  const selectImage = (event) => {
    event.stopPropagation();
    if (!editor || typeof getPos !== "function") return;

    const position = getPos();
    if (!Number.isInteger(position)) return;

    const transaction = editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, position));
    editor.view.dispatch(transaction);
    editor.view.focus();
  };

  const startResize = (event, direction) => {
    event.preventDefault();
    event.stopPropagation();
    if (!imgRef.current) return;

    const rect = imgRef.current.getBoundingClientRect();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
      ratio: rect.width / rect.height,
      direction,
      nextWidth: rect.width,
      nextHeight: rect.height,
    };
    setIsResizing(true);

    const onMove = (moveEvent) => {
      if (!dragState.current) return;
      const deltaX = moveEvent.clientX - dragState.current.startX;
      const deltaY = moveEvent.clientY - dragState.current.startY;
      let delta = 0;

      if (dragState.current.direction.includes("left")) delta = -deltaX;
      else if (dragState.current.direction.includes("right")) delta = deltaX;
      else if (dragState.current.direction.includes("top")) delta = -deltaY;
      else if (dragState.current.direction.includes("bottom")) delta = deltaY;

      const nextWidth = clampImageWidth(dragState.current.startWidth + delta);
      const nextHeight = Math.max(80, nextWidth / dragState.current.ratio);
      dragState.current.nextWidth = Math.round(nextWidth);
      dragState.current.nextHeight = Math.round(nextHeight);
      setLocalSize({
        width: dragState.current.nextWidth,
        height: dragState.current.nextHeight,
      });
    };

    const onUp = () => {
      if (!dragState.current) return;
      updateAttributes({
        width: dragState.current.nextWidth,
        height: dragState.current.nextHeight,
      });
      dragState.current = null;
      setIsResizing(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const width = localSize.width || node.attrs.width;
  const height = localSize.height || node.attrs.height;
  const cssWidth = cssSizeValue(width);
  const cssHeight = cssSizeValue(height, { clampPixels: false });
  const sizeStyle = {
    width: cssWidth,
    height: cssHeight || (cssWidth ? "auto" : undefined),
  };

  const handles = useMemo(
    () => ["top-left", "top", "top-right", "right", "bottom-right", "bottom", "bottom-left", "left"],
    []
  );

  return (
    <NodeViewWrapper
      className="tiptap-resizable-image"
      data-resize-state={isResizing ? "true" : "false"}
      style={wrapperStyleForAlign(node.attrs.align)}
      onClick={selectImage}
    >
      <div data-resize-wrapper className={selected ? "tiptap-image-selected" : ""}>
        <img
          ref={imgRef}
          src={node.attrs.src}
          alt={node.attrs.alt || ""}
          title={node.attrs.title || ""}
          style={{ ...sizeStyle, margin: 0, display: "block", maxWidth: "100%" }}
        />
        {handles.map((direction) => (
          <button
            key={direction}
            type="button"
            data-resize-handle={direction}
            onMouseDown={(event) => startResize(event, direction)}
            aria-label={`Resize image ${direction}`}
          />
        ))}
      </div>
    </NodeViewWrapper>
  );
}

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => {
          const widthAttr = element.getAttribute("width");
          const normalizedWidthAttr = normalizeImageSize(widthAttr);
          if (normalizedWidthAttr) return normalizedWidthAttr;
          const style = element.getAttribute("style") || "";
          return normalizeImageSize(stylePropertyValue(style, "width"));
        },
        renderHTML: (attrs) => {
          const width = cssSizeValue(attrs.width);
          return width ? { style: `width: ${width}` } : {};
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const heightAttr = element.getAttribute("height");
          const normalizedHeightAttr = normalizeImageSize(heightAttr, { clampPixels: false });
          if (normalizedHeightAttr) return normalizedHeightAttr;
          const style = element.getAttribute("style") || "";
          return normalizeImageSize(stylePropertyValue(style, "height"), { clampPixels: false });
        },
        renderHTML: (attrs) => {
          const height = cssSizeValue(attrs.height, { clampPixels: false });
          if (height) return { style: `height: ${height}` };
          return attrs.width ? { style: "height: auto" } : {};
        },
      },
      align: {
        default: "none",
        parseHTML: (element) => {
          const dataAlign = element.getAttribute("data-align");
          if (dataAlign) return dataAlign;
          const className = element.getAttribute("class") || "";
          if (/\bfr-fil\b/.test(className)) return "left";
          if (/\bfr-fir\b/.test(className)) return "right";
          if (/\bfr-fic\b/.test(className)) return "center";
          const style = element.getAttribute("style") || "";
          if (/float:\s*left/i.test(style)) return "left";
          if (/float:\s*right/i.test(style)) return "right";
          if (/margin-left:\s*auto/i.test(style) && /margin-right:\s*auto/i.test(style)) return "center";
          return "none";
        },
        renderHTML: (attrs) => {
          if (!attrs.align || attrs.align === "none") return {};
          return {
            "data-align": attrs.align,
            style: styleObjectToString(wrapperStyleForAlign(attrs.align)),
          };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },
});

// --- Pasted and dropped pictures (Trello #566) ---
// The old editor stored pasted pictures as base64 inside the HTML, which made
// descriptions too large for Visit Widget. Picture files are now uploaded like
// the toolbar's image button, and base64 pictures in pasted HTML are left out.
const INLINE_IMAGE_SRC = /^\s*data:/i;

async function uploadImageFile(file, imageUploadUrl) {
  const formData = new FormData();
  formData.append("file", file);
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (csrfToken) formData.append("authenticity_token", csrfToken);
  const response = await fetch(imageUploadUrl || "/admin/image_upload", {
    method: "POST",
    body: formData,
    headers: { Accept: "application/json" },
  });
  const data = await response.json();
  if (data.status !== "OK") throw new Error(data.message || "Unknown error");
  return data.link;
}

function imageFilesFrom(dataTransfer) {
  return Array.from(dataTransfer?.files || []).filter((file) => file.type.startsWith("image/"));
}

// Word, Pages and Google Docs put a picture of the copied selection on the
// clipboard next to the text. When there is text, the text is what was meant.
function hasPastedText(dataTransfer) {
  const types = Array.from(dataTransfer?.types || []);
  return types.some((type) => {
    if (type !== "text/plain" && type !== "text/html") return false;
    return dataTransfer.getData(type).trim() !== "";
  });
}

function insertUploadedImages(view, files, imageUploadUrl, pos) {
  files.forEach(async (file) => {
    try {
      const src = await uploadImageFile(file, imageUploadUrl);
      const node = view.state.schema.nodes.image.create({ src });
      const tr = pos == null
        ? view.state.tr.replaceSelectionWith(node)
        : view.state.tr.insert(Math.min(pos, view.state.doc.content.size), node);
      view.dispatch(tr);
    } catch (err) {
      alert("Image upload failed: " + err.message);
    }
  });
}

function removeInlineImages(html) {
  if (!html || !/data:/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const inline = Array.from(doc.querySelectorAll("img")).filter((img) =>
    INLINE_IMAGE_SRC.test(img.getAttribute("src") || "")
  );
  if (inline.length === 0) return html;
  inline.forEach((img) => img.remove());
  setTimeout(() => alert("Pictures can't be pasted into the text. Use the image button in the toolbar to upload them."), 0);
  return doc.body.innerHTML;
}

// --- Toolbar Components ---
function MenuButton({ onClick, isActive, title, disabled, children }) {
  const classes = [
    "tiptap-toolbar-btn",
    isActive ? "active" : "",
    disabled ? "disabled" : "",
  ].filter(Boolean).join(" ");
  return (
    <button type="button" onClick={onClick} title={title} disabled={disabled} className={classes}>
      {children}
    </button>
  );
}

function ColorPicker({ editor }) {
  const [open, setOpen] = useState(false);
  const currentColor = editor.getAttributes("textStyle").color || "#181e20";

  return (
    <div className="tiptap-color-picker" style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="tiptap-toolbar-btn"
        title="Text color"
        onClick={() => setOpen(!open)}
        style={{ position: "relative" }}
      >
        A
        <span
          style={{
            display: "block",
            height: "3px",
            width: "100%",
            backgroundColor: currentColor,
            position: "absolute",
            bottom: "2px",
            left: 0,
          }}
        />
      </button>
      {open && (
        <div className="tiptap-color-dropdown">
          {AIANTA_PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              className="tiptap-color-swatch"
              style={{ backgroundColor: color }}
              title={color}
              onClick={() => {
                editor.chain().focus().setColor(color).run();
                setOpen(false);
              }}
            />
          ))}
          <div className="tiptap-color-custom">
            <input
              type="color"
              value={currentColor}
              onChange={(e) => {
                editor.chain().focus().setColor(e.target.value).run();
                setOpen(false);
              }}
              title="Custom color"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function HighlightPicker({ editor }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="tiptap-color-picker" style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className={`tiptap-toolbar-btn ${editor.isActive("highlight") ? "active" : ""}`}
        title="Background color"
        onClick={() => setOpen(!open)}
      >
        BG
      </button>
      {open && (
        <div className="tiptap-color-dropdown">
          {AIANTA_PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              className="tiptap-color-swatch"
              style={{ backgroundColor: color }}
              title={color}
              onClick={() => {
                editor.chain().focus().toggleHighlight({ color }).run();
                setOpen(false);
              }}
            />
          ))}
          <button
            type="button"
            className="tiptap-color-swatch tiptap-color-clear"
            title="Remove highlight"
            onClick={() => {
              editor.chain().focus().unsetHighlight().run();
              setOpen(false);
            }}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}

function ImageControls({ editor }) {
  if (!editor?.isActive("image")) return null;

  const attrs = editor.getAttributes("image");
  const currentAlign = attrs.align || "none";

  const updateImage = (nextAttrs) => {
    editor.chain().focus().updateAttributes("image", nextAttrs).run();
  };

  const setImageWidth = (width) => {
    updateImage({ width, height: null });
  };

  const applyPixelWidth = (value) => {
    if (value === "") {
      setImageWidth(null);
      return;
    }

    const numericValue = parseInt(value, 10);
    if (Number.isNaN(numericValue)) return;
    setImageWidth(clampImageWidth(numericValue));
  };

  return (
    <div className="tiptap-image-controls">
      <span className="tiptap-image-controls-label">Wrap</span>
      <MenuButton
        onClick={() => updateImage({ align: "left" })}
        isActive={currentAlign === "left"}
        title="Float image left"
      >
        Left
      </MenuButton>
      <MenuButton
        onClick={() => updateImage({ align: "center" })}
        isActive={currentAlign === "center"}
        title="Center image"
      >
        Center
      </MenuButton>
      <MenuButton
        onClick={() => updateImage({ align: "right" })}
        isActive={currentAlign === "right"}
        title="Float image right"
      >
        Right
      </MenuButton>
      <MenuButton
        onClick={() => updateImage({ align: "none" })}
        isActive={currentAlign === "none"}
        title="No text wrap"
      >
        None
      </MenuButton>

      <span className="tiptap-toolbar-separator" />
      <span className="tiptap-image-controls-label">Width</span>
      {IMAGE_WIDTH_PRESETS.map((preset) => (
        <MenuButton
          key={preset.value}
          onClick={() => setImageWidth(preset.value)}
          isActive={attrs.width === preset.value}
          title={`Set image width to ${preset.label}`}
        >
          {preset.label}
        </MenuButton>
      ))}
      <input
        key={`image-width-${attrs.width || "original"}`}
        type="number"
        min={MIN_IMAGE_WIDTH}
        max={MAX_IMAGE_WIDTH}
        step="10"
        defaultValue={pixelWidthValue(attrs.width)}
        onBlur={(event) => applyPixelWidth(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          applyPixelWidth(event.currentTarget.value);
        }}
        className="tiptap-image-size-input"
        aria-label="Image width in pixels"
      />
      <span className="tiptap-image-size-unit">px</span>
      <MenuButton onClick={() => setImageWidth(null)} title="Use original image width">
        Original
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().deleteSelection().run()} title="Remove image">
        Remove
      </MenuButton>
    </div>
  );
}

// --- Full Toolbar ---
function FullToolbar({ editor, imageUploadUrl }) {
  const setLink = () => {
    const current = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", current || "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  };

  const insertImage = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".jpg,.jpeg,.gif,.png";
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const src = await uploadImageFile(file, imageUploadUrl);
        editor.chain().focus().setImage({ src }).run();
      } catch (err) {
        alert("Image upload failed: " + err.message);
      }
    };
    fileInput.click();
  };

  const currentFontSize = editor.getAttributes("textStyle").fontSize || "";
  const currentFontFamily = editor.getAttributes("textStyle").fontFamily || "";

  return (
    <div className="tiptap-toolbar">
      <div className="tiptap-toolbar-group">
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title="Bold"
        >
          <strong>B</strong>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title="Italic"
        >
          <em>I</em>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive("underline")}
          title="Underline"
        >
          <u>U</u>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive("strike")}
          title="Strikethrough"
        >
          <s>S</s>
        </MenuButton>

        <span className="tiptap-toolbar-separator" />

        <MenuButton
          onClick={setLink}
          isActive={editor.isActive("link")}
          title="Insert link"
        >
          Link
        </MenuButton>

        <span className="tiptap-toolbar-separator" />

        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title="Bullet list"
        >
          &bull; List
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          title="Numbered list"
        >
          1. List
        </MenuButton>

        <MenuButton
          onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
          isActive={false}
          title="Indent"
        >
          &rarr;|
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().liftListItem("listItem").run()}
          isActive={false}
          title="Outdent"
        >
          |&larr;
        </MenuButton>

        <span className="tiptap-toolbar-separator" />

        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          isActive={editor.isActive({ textAlign: "left" })}
          title="Align left"
        >
          Left
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          isActive={editor.isActive({ textAlign: "center" })}
          title="Align center"
        >
          Center
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          isActive={editor.isActive({ textAlign: "right" })}
          title="Align right"
        >
          Right
        </MenuButton>

        <span className="tiptap-toolbar-separator" />

        <MenuButton onClick={insertImage} isActive={false} title="Insert image">
          Image
        </MenuButton>
      </div>

      <div className="tiptap-toolbar-group">
        <select
          className="tiptap-toolbar-select"
          value={currentFontFamily}
          onChange={(e) => {
            if (e.target.value) {
              editor.chain().focus().setFontFamily(e.target.value).run();
            } else {
              editor.chain().focus().unsetFontFamily().run();
            }
          }}
          title="Font family"
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>

        <select
          className="tiptap-toolbar-select"
          value={currentFontSize}
          onChange={(e) => {
            if (e.target.value) {
              editor.chain().focus().setFontSize(e.target.value).run();
            } else {
              editor.chain().focus().unsetFontSize().run();
            }
          }}
          title="Font size"
        >
          <option value="">Size</option>
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <ColorPicker editor={editor} />
        <HighlightPicker editor={editor} />
      </div>
      <ImageControls editor={editor} />
    </div>
  );
}

// --- Minimal Toolbar ---
function MinimalToolbar({ editor }) {
  const setLink = () => {
    const current = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", current || "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="tiptap-toolbar">
      <div className="tiptap-toolbar-group">
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title="Bold"
        >
          <strong>B</strong>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title="Italic"
        >
          <em>I</em>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title="Bullet list"
        >
          &bull; List
        </MenuButton>
        <MenuButton
          onClick={setLink}
          isActive={editor.isActive("link")}
          title="Insert link"
        >
          Link
        </MenuButton>
      </div>
    </div>
  );
}

// --- Main TiptapEditor Component ---
export default function TiptapEditor({
  inputId,
  label,
  toolbar = "full",
  wordCounterMax,
  imageUploadUrl,
}) {
  const input = document.getElementById(inputId);
  const initialContent = input?.value || "";

  const wrapperRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        underline: false,
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Underline,
      Highlight.configure({ multicolor: true }),
      ResizableImage.configure({ inline: true, allowBase64: true }),
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Start writing..." }),
      ...(wordCounterMax
        ? [CharacterCount.configure({ mode: "textSize" })]
        : []),
    ],
    shouldRerenderOnTransaction: true,
    immediatelyRender: true,
    content: sanitizeEditorHtml(initialContent),
    editorProps: {
      transformPastedHTML: (html) => removeInlineImages(sanitizeEditorHtml(html)),
      handlePaste: (view, event) => {
        if (hasPastedText(event.clipboardData)) return false;
        const files = imageFilesFrom(event.clipboardData);
        if (files.length === 0) return false;
        event.preventDefault();
        insertUploadedImages(view, files, imageUploadUrl);
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const files = imageFilesFrom(event.dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        insertUploadedImages(view, files, imageUploadUrl, coords?.pos);
        return true;
      },
    },
    onUpdate: ({ editor: tiptap }) => {
      const el = document.getElementById(inputId);
      if (el) {
        const html = tiptap.getHTML();
        el.value = html === "<p></p>" ? "" : html;
      }
    },
  });

  // Listen for external content updates (e.g., from GPT integration)
  useEffect(() => {
    const container = wrapperRef.current?.closest("[data-tiptap-editor]");
    if (!container || !editor) return;

    const handler = (e) => {
      const html = e.detail?.html;
      if (html != null) {
        const sanitizedHtml = sanitizeEditorHtml(html);
        editor.commands.setContent(sanitizedHtml);
        const el = document.getElementById(inputId);
        if (el) el.value = sanitizedHtml;
      }
    };

    container.addEventListener("tiptap:setContent", handler);
    return () => container.removeEventListener("tiptap:setContent", handler);
  }, [editor, inputId]);

  if (!editor) return null;

  return (
    <div className="tiptap-editor-wrapper" ref={wrapperRef}>
      {label && <div className="tiptap-editor-label">{label}</div>}
      {toolbar === "minimal" ? (
        <MinimalToolbar editor={editor} />
      ) : (
        <FullToolbar editor={editor} imageUploadUrl={imageUploadUrl} />
      )}
      <div className="tiptap">
        <EditorContent editor={editor} />
      </div>
      {wordCounterMax && editor.storage.characterCount ? (
        <div
          className={`tiptap-word-counter ${
            editor.storage.characterCount.words() > wordCounterMax ? "tiptap-word-counter-over" : ""
          }`}
        >
          {editor.storage.characterCount.words()} / {wordCounterMax} words
        </div>
      ) : null}
    </div>
  );
}
